import { useQuery } from "@tanstack/react-query";
// rag-service.js is plain JS, so this import is implicitly typed `any`.
import { projectsApi } from "../../../services/rag-service";
import { useOrganization } from "../../../contexts/OrganizationContext";
// requestScope.js is plain JS, so this import is implicitly typed `any`.
import { getActiveOrganizationId } from "../../../utils/requestScope";

export type Project = {
  id: string;
  name?: string;
  displayName?: string;
};

export const PROJECTS_QUERY_KEY = ["mcps-projects"] as const;

const FIVE_MINUTES = 5 * 60 * 1000;

type Options = {
  enabled?: boolean;
};

const sortProjects = (projects: Project[]): Project[] =>
  [...projects].sort((left, right) => {
    const leftName = left.name ?? left.displayName ?? "";
    const rightName = right.name ?? right.displayName ?? "";
    return leftName.localeCompare(rightName);
  });

export const useProjects = ({ enabled = true }: Options = {}) => {
  // The request is org-scoped by the X-Organization-ID header, so the org id
  // belongs in the cache key too — otherwise switching orgs keeps serving the
  // previous org's projects until the entry goes stale. Mirror the header's
  // own source: context once loaded, localStorage during the initial load,
  // and "personal" when neither is set (personal workspace sends no header).
  const { currentOrganization } = useOrganization();
  const orgScope =
    currentOrganization?.id ?? getActiveOrganizationId() ?? "personal";

  return useQuery<Project[]>({
    queryKey: [...PROJECTS_QUERY_KEY, orgScope],
    queryFn: async () => {
      const projects = await projectsApi.getProjects({ includeShared: true });
      return Array.isArray(projects) ? sortProjects(projects as Project[]) : [];
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
};
